import React, { useEffect, useState } from "react";
import { useToasts } from "react-toast-notifications";
import { Form, Input, Button } from "antd";
import "./ContactUsPage.css";
import { validateConfig } from "../../lib/config";
import { mintToken } from "../../lib/mint";
import { submitToMailer } from "../../lib/mailer";

function ContactUs() {
  const [formData, setFormData] = useState<any>({});
  const { addToast } = useToasts();
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  // reCAPTCHA v3 uses programmatic token acquisition; no visible widget state

  const CONTACT_API_BASE = process.env.REACT_APP_CONTACT_API_BASE;
  const RECAPTCHA_SITE_KEY = process.env.REACT_APP_CONTACT_RECAPTCHA_SITE_KEY;

  // Captcha token will be obtained via lib/mint
  const loadRecaptcha = () => {
    if ((window as any).grecaptcha) return;
    if (!RECAPTCHA_SITE_KEY) return;
    const existing = document.querySelector('script[src^="https://www.google.com/recaptcha/api.js"]');
    if (existing) return;
    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  };

  useEffect(() => {
    loadRecaptcha();
    return () => {
      // no-op cleanup
    };
  }, []);

  const inputOnChange = (key: string, value: string) => {
    formData[key] = value;
    setFormData(formData);
  };

  const friendlyError = (status: number | null, fallback: string, serverMessage?: string) => {
    if (serverMessage) return serverMessage;
    if (status === 401 || status === 403) return "Unauthorized or forbidden. Please complete the captcha and try again.";
    if (status === 429) return "Rate limit exceeded. Please wait a bit and try again.";
    if (status === 409) return "This submission was already used. Edit your message and try again.";
    return fallback;
  };

  const onFinish = async (values: any) => {
    try {
      validateConfig();
    } catch (e) {
      addToast((e as Error).message, { appearance: "error", autoDismiss: true });
      return;
    }

    const formObject = {
      firstName: values.firstName,
      lastName: values.lastName,
      subject: values.titleAndCompany,
      email: values.email + " - " + values.phone,
      message: values.fullMessage,
    };

    setIsSubmitting(true);
    try {
      // Step A: mint (includes captcha + optional DPoP cnf)
      const { token, context } = await mintToken({
        firstName: values.firstName,
        lastName: values.lastName,
        subject: values.titleAndCompany,
        email: values.email + " - " + values.phone,
        message: values.fullMessage,
      });

      // Step B: submit
      const submitResponse = await submitToMailer({
        firstName: values.firstName,
        lastName: values.lastName,
        subject: values.titleAndCompany,
        email: values.email + " - " + values.phone,
        message: values.fullMessage,
      }, token, context);

      let payload: any = undefined;
      try { payload = await submitResponse.json(); } catch { }

      if (submitResponse.status === 202 && payload && payload.accepted) {
        addToast("Your message has been received. Thank you for contacting us.", { appearance: "success", autoDismiss: true });
        form.resetFields();
        setFormData({});
        setIsSubmitting(false);
        return;
      }

      addToast("We couldn't send your message right now. Please try again later.", { appearance: "error", autoDismiss: true });
      setIsSubmitting(false);
    } catch (e) {
      addToast("Network error. Please check your connection and try again.", { appearance: "error", autoDismiss: true });
      setIsSubmitting(false);
    }
  };

  const onFinishFailed = (errorInfo: any) => {
    console.log("Failed:", errorInfo);
  };

  return (
    <div className="contact">
      <div className="contact-form-section">
        <div className="container">
          <div className="contact-form-section-form flex-column">
            <Form
              form={form}
              name="basic"
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
            >
              <h1>Get in touch with us</h1>
              <div className="flex-row">
                <Form.Item
                  name="firstName"
                  rules={[
                    {
                      required: true,
                      message: "Please input your First Name!",
                    },
                  ]}
                >
                  <Input
                    className="contact-input"
                    placeholder={`First name`}
                    value={formData.firstName}
                    allowClear
                    onChange={(e: any) => {
                      inputOnChange("firstName", e.target.value);
                    }}
                  />
                </Form.Item>
                <Form.Item
                  name="lastName"
                  rules={[
                    { required: true, message: "Please input your Last Name!" },
                  ]}
                >
                  <Input
                    className="contact-input"
                    placeholder={`Last name`}
                    value={formData.lastName}
                    allowClear
                    onChange={(e: any) => {
                      inputOnChange("lastName", e.target.value);
                    }}
                  />
                </Form.Item>
              </div>
              <div className="flex-row">
                <Form.Item
                  name="titleAndCompany"
                  rules={[
                    {
                      required: true,
                      message: "Please input your Title/Company!",
                    },
                  ]}
                >
                  <Input
                    className="contact-input"
                    placeholder={`Title/Company`}
                    value={formData.titleAndCompany}
                    allowClear
                    onChange={(e: any) => {
                      inputOnChange("titleAndCompany", e.target.value);
                    }}
                  />
                </Form.Item>
              </div>
              <div className="flex-row">
                <Form.Item
                  name="email"
                  rules={[
                    {
                      required: true,
                      message: "Please input your Email!",
                      type: "email",
                    },
                  ]}
                >
                  <Input
                    className="contact-input"
                    placeholder={`Email`}
                    value={formData.email}
                    allowClear
                    onChange={(e: any) => {
                      inputOnChange("email", e.target.value);
                    }}
                  />
                </Form.Item>
              </div>
              <div className="flex-row">
                <Form.Item
                  name="phone"
                  rules={[
                    { required: true, message: "Please input your Phone!" },
                  ]}
                >
                  <Input
                    className="contact-input"
                    placeholder={`Phone number`}
                    value={formData.phone}
                    allowClear
                    onChange={(e: any) => {
                      inputOnChange("phone", e.target.value);
                    }}
                  />
                </Form.Item>
              </div>
              <div className="flex-row">
                <Form.Item
                  name="fullMessage"
                  rules={[
                    { required: true, message: "Please input your Message!" },
                  ]}
                >
                  <Input.TextArea
                    autoSize={{ minRows: 5, maxRows: 10 }}
                    className="contact-input"
                    value={formData.fullMessage}
                    placeholder={`Message`}
                    onChange={(e: any) => {
                      inputOnChange("fullMessage", e.target.value);
                    }}
                  />
                </Form.Item>
              </div>
              <div className="flex-row">
                <Form.Item>
                  <Button
                    className="contact-form-submit"
                    type="primary"
                    size="large"
                    htmlType="submit"
                    disabled={isSubmitting}
                    loading={isSubmitting}
                  >
                    {isSubmitting ? "Sending…" : "Submit"}
                  </Button>
                </Form.Item>
              </div>
            </Form>
          </div>
        </div>
      </div>
      <div className="locations-section">
        <div className="container">
          <div className="locations-section-heading">
            <h2 className="align-center">Office locations</h2>
          </div>
          <div className="locations-section-row flex-row">
            <div className="locations-section-column">
              <img className="locations-section-column-icon" src="/images/egypt-flag.png" height="30" width="30" alt="" />
              <h3>Head office</h3>

              <p> 167 Emtedad Ramsis 2 Apt.32, Nasr City, Cairo</p>

              <p>
                Tel: <a href="tel:+20220816755">+(202) 20816755</a> /
                <a href="tel:+20220816483">+(202) 20816483</a>
              </p>
            </div>
            <div className="locations-section-column">
              <img className="locations-section-column-icon" src="/images/egypt-flag.png" height="30" width="30" alt="" />
              <h3>Operational branch</h3>

              <p> 1180 Zahraa Nasr City Apt.13, Cairo</p>

              <p>
                Tel:<a href="tel:+20224127491">+(202) 24127491</a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ContactUs;
